import { useMemo, useState } from "react";
import { theme } from "../styles/theme";
import { calcularCarga } from "../utils/docentes";

const ANIOS_DISPONIBLES = ["2024", "2025", "2026", "2027"];
const ESTADO_PROPUESTA = "PROPUESTA DE TEMAS Y TEMARIOS";

const normalizarTexto = (valor) => String(valor ?? "").trim().toUpperCase();

const construirSerie = (data) =>
  calcularCarga(data)
    .map((docente) => ({
      nombre: docente.nombre,
      tutor: docente.director || 0,
      evaluador: docente.evaluador || 0,
      total: (docente.director || 0) + (docente.evaluador || 0)
    }))
    .filter((docente) => docente.total > 0)
    .sort(
      (a, b) =>
        b.tutor - a.tutor ||
        b.evaluador - a.evaluador ||
        b.total - a.total ||
        a.nombre.localeCompare(b.nombre, "es")
    );

function TablasDocentes({ data }) {
  const [anioActivo, setAnioActivo] = useState("2026");

  const dataAnual = useMemo(
    () => data.filter((item) => String(item?.anio ?? "").trim() === anioActivo),
    [anioActivo, data]
  );

  const series = useMemo(() => {
    const graduados = dataAnual.filter((item) => normalizarTexto(item?.estado) === "GRADUADO");
    const propuestas = dataAnual.filter((item) => normalizarTexto(item?.estado) === ESTADO_PROPUESTA);

    return [
      {
        id: "general",
        titulo: "Participaciones totales del año",
        descripcion: "Tutor y evaluador por docente en todos los registros del año seleccionado.",
        data: construirSerie(dataAnual)
      },
      {
        id: "graduados",
        titulo: "Participaciones en graduados",
        descripcion: "Tutor y evaluador considerando solo registros con estado Graduado.",
        data: construirSerie(graduados)
      },
      {
        id: "propuestas",
        titulo: "Participaciones en propuesta de temas y temarios",
        descripcion: "Tutor y evaluador considerando solo registros en propuesta de temas y temarios.",
        data: construirSerie(propuestas)
      }
    ];
  }, [dataAnual]);

  return (
    <div style={styles.page}>
      <section style={styles.hero}>
        <div>
          <h2 style={styles.title}>Reporte anual</h2>
          <p style={styles.subtitle}>
            Selecciona un año para revisar las participaciones de tutor y evaluador por docente.
          </p>
        </div>

        <div style={styles.selectorWrap}>
          <label style={styles.selectorLabel}>
            Año
            <select value={anioActivo} onChange={(event) => setAnioActivo(event.target.value)} style={styles.select}>
              {ANIOS_DISPONIBLES.map((anio) => (
                <option key={anio} value={anio}>
                  {anio}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {series.map((serie) => (
        <ChartSection key={serie.id} serie={serie} />
      ))}
    </div>
  );
}

function ChartSection({ serie }) {
  const maxValue = Math.max(1, ...serie.data.flatMap((item) => [item.tutor, item.evaluador]));

  return (
    <section style={styles.panel}>
      <div style={styles.panelHeader}>
        <h3 style={styles.panelTitle}>{serie.titulo}</h3>
        <p style={styles.panelSubtitle}>{serie.descripcion}</p>
      </div>

      {serie.data.length > 0 ? (
        <>
          <div style={styles.legend}>
            <div style={styles.legendItem}>
              <span style={{ ...styles.legendSwatch, background: theme.colors.primary }} />
              <span style={styles.legendText}>Tutor</span>
            </div>
            <div style={styles.legendItem}>
              <span style={{ ...styles.legendSwatch, background: theme.colors.accent }} />
              <span style={styles.legendText}>Evaluador</span>
            </div>
          </div>

          <div style={styles.chartScroller}>
            <div style={styles.chart}>
              {serie.data.map((item) => (
                <div key={item.nombre} style={styles.group}>
                  <div style={styles.barArea}>
                    <div
                      title={`Tutor: ${item.tutor}`}
                      style={{
                        ...styles.bar,
                        ...styles.barTutor,
                        height: `${Math.max((item.tutor / maxValue) * 150, item.tutor > 0 ? 8 : 0)}px`
                      }}
                    >
                      {item.tutor > 0 && <span style={styles.barValue}>{item.tutor}</span>}
                    </div>
                    <div
                      title={`Evaluador: ${item.evaluador}`}
                      style={{
                        ...styles.bar,
                        ...styles.barEvaluador,
                        height: `${Math.max((item.evaluador / maxValue) * 150, item.evaluador > 0 ? 8 : 0)}px`
                      }}
                    >
                      {item.evaluador > 0 && <span style={styles.barValue}>{item.evaluador}</span>}
                    </div>
                  </div>
                  <div style={styles.groupLabel} title={item.nombre}>{item.nombre}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div style={styles.emptyState}>
          <strong style={styles.emptyTitle}>No hay datos para esta vista</strong>
          <p style={styles.emptyText}>Prueba con otro año o revisa si los registros tienen el campo Año completo.</p>
        </div>
      )}
    </section>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "24px"
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: "20px",
    padding: "24px",
    borderRadius: "20px",
    background: "linear-gradient(135deg, #ffffff 0%, #eef4ff 100%)",
    border: `1px solid ${theme.colors.border}`,
    boxShadow: "0 14px 35px rgba(18, 38, 63, 0.08)"
  },
  title: {
    margin: 0,
    color: theme.colors.text,
    fontSize: "28px"
  },
  subtitle: {
    margin: "8px 0 0",
    color: theme.colors.subtext,
    lineHeight: 1.5,
    maxWidth: "760px"
  },
  selectorWrap: {
    minWidth: "180px"
  },
  selectorLabel: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    color: theme.colors.subtext,
    fontSize: "14px",
    fontWeight: 700
  },
  select: {
    background: "#f8fbff",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "10px",
    padding: "12px 14px",
    color: theme.colors.text,
    outline: "none"
  },
  panel: {
    background: theme.colors.panelBg,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "20px",
    padding: "22px",
    boxShadow: "0 14px 35px rgba(18, 38, 63, 0.08)",
    display: "flex",
    flexDirection: "column",
    gap: "18px"
  },
  panelHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  panelTitle: {
    margin: 0,
    color: theme.colors.text,
    fontSize: "20px"
  },
  panelSubtitle: {
    margin: 0,
    color: theme.colors.subtext,
    lineHeight: 1.5
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px"
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  legendSwatch: {
    width: "14px",
    height: "14px",
    borderRadius: "4px"
  },
  legendText: {
    color: theme.colors.subtext,
    fontSize: "13px",
    fontWeight: 700
  },
  chartScroller: {
    overflowX: "auto",
    paddingBottom: "6px"
  },
  chart: {
    display: "flex",
    alignItems: "flex-end",
    gap: "2px",
    minWidth: "100%",
    width: "fit-content",
    paddingTop: "4px",
    paddingBottom: "76px"
  },
  group: {
    width: "38px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "2px",
    flexShrink: 0,
    position: "relative"
  },
  barArea: {
    height: "166px",
    width: "100%",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: "2px",
    padding: "0 1px",
    borderBottom: `1px solid ${theme.colors.border}`
  },
  bar: {
    width: "13px",
    minHeight: "0",
    borderRadius: "6px 6px 0 0",
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    boxShadow: "0 10px 20px rgba(18, 38, 63, 0.14)"
  },
  barTutor: {
    background: `linear-gradient(180deg, ${theme.colors.primary} 0%, #1d4ed8 100%)`
  },
  barEvaluador: {
    background: `linear-gradient(180deg, ${theme.colors.accent} 0%, #b7791f 100%)`
  },
  barValue: {
    position: "absolute",
    top: "-18px",
    color: theme.colors.text,
    fontSize: "9px",
    fontWeight: 800
  },
  groupLabel: {
    width: "70px",
    color: theme.colors.subtext,
    fontSize: "9px",
    fontWeight: 700,
    lineHeight: 1.1,
    whiteSpace: "nowrap",
    transform: "rotate(52deg)",
    transformOrigin: "top left",
    textAlign: "left",
    position: "absolute",
    top: "170px",
    left: "20px",
    pointerEvents: "none"
  },
  emptyState: {
    border: `1px dashed ${theme.colors.border}`,
    borderRadius: "16px",
    padding: "28px",
    background: "#fbfcfd"
  },
  emptyTitle: {
    display: "block",
    color: theme.colors.text,
    marginBottom: "8px"
  },
  emptyText: {
    margin: 0,
    color: theme.colors.subtext,
    lineHeight: 1.5
  }
};

export default TablasDocentes;
